import { WORKFLOW_FIRST_PATH, WORKFLOW_LIMITS } from "@sagot/constants";
import { useTranslation } from "@sagot/i18n";
import { Button, Icon, Input } from "@sagot/ui";
import { useState } from "react";
import { freshButtonId } from "../definition-model";
import type { ReplyButton, ReplyButtonsStep } from "../types";
import { CountedTextarea } from "./counted-field";
import type { FlowContext } from "./flow-context";
import { PathPicker } from "./path-select";
import { BUTTON_TARGETS } from "./reply-model";
import { ReplySaveField } from "./reply-save-field";
import { SwitchRow } from "./switch-row";
import { VariableList, type FormProps } from "./webhook-form";

const F = "engage.workflows.fields";

interface ButtonRowProps extends Pick<FlowContext, "paths" | "canAddPath"> {
  button: ReplyButton;
  index: number;
  count: number;
  disabled: boolean;
  onChange: (patch: Partial<ReplyButton>) => void;
  onMove: (to: number) => void;
  onRemove: () => void;
}

/** One button: its label, where it leads, and (when the label is long) the shorter label Messenger shows. */
function ButtonRow({ button, index, count, disabled, paths, canAddPath, onChange, onMove, onRemove }: ButtonRowProps) {
  const { t } = useTranslation();
  const n = index + 1;
  const [shortLabel, setShortLabel] = useState(button.messenger_label !== undefined);
  const tooLong = button.label.length > WORKFLOW_LIMITS.messengerLabel;
  return (
    <li className="space-y-2 rounded-md border border-line p-3">
      <div className="flex flex-wrap items-center gap-2">
        <Input
          aria-label={t(`${F}.buttonLabelN`, { n })}
          className="h-9 min-w-40 flex-1 text-sm"
          maxLength={WORKFLOW_LIMITS.buttonLabel}
          disabled={disabled}
          value={button.label}
          onChange={(e) => { onChange({ label: e.target.value }); }}
        />
        <PathPicker
          label={t(`${F}.buttonGoesToN`, { n })}
          className="h-9 w-36 text-sm"
          value={button.goto || undefined}
          paths={paths}
          canAddPath={canAddPath}
          emptyLabel={t(`${F}.choosePath`)}
          disabled={disabled}
          onChange={(goto) => { onChange({ goto: goto ?? "" }); }}
        />
        <div className="flex gap-0.5">
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === 0} aria-label={t(`${F}.moveButtonUp`, { n })} onClick={() => { onMove(index - 1); }}>
            <Icon name="chevron-up" />
          </Button>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || index === count - 1} aria-label={t(`${F}.moveButtonDown`, { n })} onClick={() => { onMove(index + 1); }}>
            <Icon name="chevron-down" />
          </Button>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled || count <= WORKFLOW_LIMITS.buttonsMin} aria-label={t(`${F}.removeButtonN`, { n })} onClick={onRemove}>
            <Icon name="trash" />
          </Button>
        </div>
      </div>
      {shortLabel ? (
        <div className="flex items-center gap-2">
          <Input
            aria-label={t(`${F}.messengerLabelN`, { n })}
            className="h-9 flex-1 text-sm"
            maxLength={WORKFLOW_LIMITS.messengerLabel}
            placeholder={t(`${F}.messengerLabelPlaceholder`)}
            disabled={disabled}
            value={button.messenger_label ?? ""}
            onChange={(e) => { onChange({ messenger_label: e.target.value || undefined }); }}
          />
          <span className="font-mono text-xs tabular-nums text-fg-subtle">{`${String((button.messenger_label ?? "").length)}/${String(WORKFLOW_LIMITS.messengerLabel)}`}</span>
          <Button type="button" variant="ghost" size="sm" iconOnly disabled={disabled} aria-label={t(`${F}.removeMessengerLabelN`, { n })} onClick={() => { setShortLabel(false); onChange({ messenger_label: undefined }); }}>
            <Icon name="x" />
          </Button>
        </div>
      ) : tooLong ? (
        <p className="text-xs text-fg-muted">
          {t(`${F}.messengerCut`, { max: WORKFLOW_LIMITS.messengerLabel })}{" "}
          <button type="button" disabled={disabled} className="font-semibold text-primary-ink underline" onClick={() => { setShortLabel(true); }}>{t(`${F}.setMessengerLabel`)}</button>
        </p>
      ) : null}
    </li>
  );
}

/** Start over and "remember the choice": the two optional behaviours of a menu. */
function ButtonOptions({ step, disabled, pathId, onChange }: FormProps<ReplyButtonsStep> & Pick<FlowContext, "pathId">) {
  const { t } = useTranslation();
  const startOver = step.start_over === true;
  const inFirstPath = pathId === WORKFLOW_FIRST_PATH;
  const full = step.buttons.length >= WORKFLOW_LIMITS.buttonsMax;
  const description = inFirstPath ? t(`${F}.startOverFirstPath`) : full && !startOver ? t(`${F}.startOverFull`, { max: WORKFLOW_LIMITS.buttonsMax }) : t(`${F}.startOverHint`);
  return (
    <div className="space-y-5">
      <SwitchRow
        label={t(`${F}.startOver`)}
        description={description}
        checked={startOver}
        disabled={disabled || (!startOver && (inFirstPath || full))}
        onChange={(on) => { onChange({ ...step, start_over: on || undefined }); }}
      />
      {startOver ? <p className="rounded-md bg-surface-2 p-3 text-xs text-fg-muted" data-testid="start-over-note">{t(`${F}.startOverGoes`, { path: WORKFLOW_FIRST_PATH })}</p> : null}
      <SwitchRow
        label={t(`${F}.saveChoice`)}
        description={t(`${F}.saveChoiceHint`)}
        checked={step.save_to !== undefined}
        disabled={disabled}
        onChange={(on) => { onChange({ ...step, save_to: on ? `var.${step.id}` : undefined }); }}
      />
      {step.save_to !== undefined ? (
        <ReplySaveField label={t(`${F}.saveChoiceTo`)} value={step.save_to} targets={BUTTON_TARGETS} attributeTypes={["text"]} fallbackKey={step.id} disabled={disabled} onChange={(save_to) => { onChange({ ...step, save_to }); }} />
      ) : null}
    </div>
  );
}

/** Reply buttons: the message, 2 to 8 buttons that each lead to a path, and whether the visitor may type instead. */
export function ReplyButtonsForm({ step, disabled, onChange, paths, canAddPath, pathId }: FormProps<ReplyButtonsStep> & Pick<FlowContext, "paths" | "canAddPath" | "pathId">) {
  const { t } = useTranslation();
  const setButton = (index: number, patch: Partial<ReplyButton>) => {
    onChange({ ...step, buttons: step.buttons.map((button, i) => (i === index ? { ...button, ...patch } : button)) });
  };
  const moveButton = (from: number, to: number) => {
    const buttons = [...step.buttons];
    const [moved] = buttons.splice(from, 1);
    if (moved) buttons.splice(to, 0, moved);
    onChange({ ...step, buttons });
  };
  return (
    <div className="space-y-5">
      <div>
        <CountedTextarea label={t(`${F}.visitorMessage`)} value={step.text} max={WORKFLOW_LIMITS.message} disabled={disabled} onChange={(text) => { onChange({ ...step, text }); }} />
        <VariableList />
      </div>
      <div>
        <p className="label">{t(`${F}.buttons`, { min: WORKFLOW_LIMITS.buttonsMin, max: WORKFLOW_LIMITS.buttonsMax })}</p>
        <ul className="space-y-2">
          {step.buttons.map((button, index) => (
            <ButtonRow
              key={button.id}
              button={button}
              index={index}
              count={step.buttons.length}
              disabled={disabled}
              paths={paths}
              canAddPath={canAddPath}
              onChange={(patch) => { setButton(index, patch); }}
              onMove={(to) => { moveButton(index, to); }}
              onRemove={() => { onChange({ ...step, buttons: step.buttons.filter((_, i) => i !== index) }); }}
            />
          ))}
        </ul>
        {step.buttons.length + (step.start_over ? 1 : 0) < WORKFLOW_LIMITS.buttonsMax ? (
          <Button type="button" variant="ghost" size="sm" className="mt-2" disabled={disabled} onClick={() => { onChange({ ...step, buttons: [...step.buttons, { id: freshButtonId(step.buttons), label: "", goto: "" }] }); }}>
            <Icon name="plus" />
            {t(`${F}.addButton`)}
          </Button>
        ) : null}
      </div>
      <SwitchRow label={t(`${F}.allowTyping`)} description={t(`${F}.allowTypingHint`)} checked={step.allow_typing} disabled={disabled} onChange={(allow_typing) => { onChange({ ...step, allow_typing }); }} />
      <ButtonOptions step={step} disabled={disabled} pathId={pathId} onChange={onChange} />
    </div>
  );
}
